// Legal Acknowledgment Gate.
//
// Modal banner that blocks the authenticated app until the user accepts the
// currently-published Terms of Service and Privacy Policy. Decision to render
// is made by the parent (App.tsx) from the server-computed `legal_status` on
// the AuthContext user, so this component only handles the *interaction*:
// fetch current versions → render checkbox → POST acceptance → on success,
// patch context legal_status and unmount.
//
// Mount placement: inside <RequireAuth> in App.tsx so unauthenticated routes
// (login/register) are unaffected. The cookie ConsentBanner (GDPR/PECR) is a
// separate, non-blocking surface and stays mounted outside RequireAuth.

import { useEffect, useState } from 'react';
import Modal from '../Modal/Modal';
import { useAuth } from '../../contexts/AuthContext';
import {
  legalAPI,
  StaleLegalVersionError,
  type LegalAcceptanceItem,
  type LegalCurrentResponse,
  type LegalDocumentType,
} from '../../api/legal';
import { EXTERNAL_LINKS } from '../../config/constants';
import { logger } from '../../utils/logger';
import styles from './LegalAcknowledgmentGate.module.css';

// The two documents the user must agree to. Order matters for display only —
// the backend writes both atomically regardless of submission order.
const DOC_ORDER: LegalDocumentType[] = ['tos', 'privacy'];

const DOC_LABEL: Record<LegalDocumentType, string> = {
  tos: 'Terms of Service',
  privacy: 'Privacy Policy',
};

const DOC_HREF: Record<LegalDocumentType, string> = {
  tos: EXTERNAL_LINKS.TERMS_OF_SERVICE,
  privacy: EXTERNAL_LINKS.PRIVACY_POLICY,
};

function getDocHref(current: LegalCurrentResponse | null, docType: LegalDocumentType): string {
  return current?.documents?.[docType]?.source_url || DOC_HREF[docType];
}

export default function LegalAcknowledgmentGate() {
  const { user, setLegalStatus } = useAuth();
  const [current, setCurrent] = useState<LegalCurrentResponse | null>(null);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Render gate iff the server says the user has unaccepted current docs.
  // Treat missing legal_status as "no gate" so older backends are a no-op.
  const required = !!user && user.legal_status && user.legal_status.all_ok === false;

  // Fetch the current published versions when the gate opens. We re-fetch on
  // every open so a freshly-bumped version is picked up without a hard reload.
  useEffect(() => {
    if (!required) {
      setCurrent(null);
      return;
    }
    let cancelled = false;
    legalAPI.getCurrent()
      .then((res) => { if (!cancelled) setCurrent(res); })
      .catch((e) => {
        logger.error('LegalAcknowledgmentGate: failed to load current docs', e);
        if (!cancelled) setError('Could not load the legal documents. Please refresh.');
      });
    return () => { cancelled = true; };
  }, [required]);

  if (!required) return null;

  // Build the acceptance payload from the freshly-fetched current docs. We
  // only emit entries for docs the server currently publishes; an unpublished
  // doc cannot block the gate (compute_legal_status returns ok for it).
  const buildAcceptances = (): LegalAcceptanceItem[] => {
    if (!current) return [];
    const out: LegalAcceptanceItem[] = [];
    for (const docType of DOC_ORDER) {
      const doc = current.documents[docType];
      if (doc?.published && doc.version && doc.sha256) {
        out.push({ document_type: docType, version: doc.version, sha256: doc.sha256 });
      }
    }
    return out;
  };

  const handleAccept = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const acceptances = buildAcceptances();
      if (acceptances.length === 0) {
        setError('No current documents to accept. Please refresh the page.');
        return;
      }
      const res = await legalAPI.accept(acceptances);
      // Optimistically patch the context so the gate unmounts immediately.
      if (res.legal_status) {
        setLegalStatus(res.legal_status);
      } else {
        // Server omitted legal_status — clear the gate optimistically by
        // marking all_ok=true; AuthContext refresh on next /auth/me reconciles.
        setLegalStatus({ all_ok: true, tos_ok: true, privacy_ok: true, current: {}, accepted: {} });
      }
    } catch (e) {
      if (e instanceof StaleLegalVersionError) {
        // Version changed under us; refetch and let the user re-confirm.
        setChecked(false);
        const refreshed = await legalAPI.getCurrent().catch(() => null);
        if (refreshed) setCurrent(refreshed);
        setError('The documents were updated. Please review the latest versions and confirm again.');
      } else {
        const msg = (e as any)?.message || 'Acceptance failed. Please try again.';
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={() => { /* intentionally blocking - no dismiss */ }}
      title="Please review and accept our updated terms"
      showCloseButton={false}
      size="medium"
      closeOnBackdrop={false}
      closeOnEscape={false}
      footer={
        <div className={styles.footer}>
          <button
            type="button"
            onClick={handleAccept}
            disabled={!checked || submitting || !current}
            className={styles.acceptButton}
            data-testid="legal-gate-accept"
          >
            {submitting ? 'Saving…' : 'Accept and continue'}
          </button>
        </div>
      }
    >
      <p className={styles.intro}>
        We’ve updated our Privacy Policy and Terms of Service. To continue using
        Lazy SEM, please review and confirm your acceptance.
      </p>
      <LegalVersionList current={current} />
      <label className={styles.checkboxLabel} data-testid="legal-gate-checkbox-label">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          disabled={submitting}
          className={styles.checkbox}
          data-testid="legal-gate-checkbox"
        />
        <span>
          I have read and agree to Lazy SEM’s{' '}
          <a className={styles.link} href={getDocHref(current, 'tos')} target="_blank" rel="noopener noreferrer">
            {DOC_LABEL.tos}
          </a>{' '}
          and{' '}
          <a className={styles.link} href={getDocHref(current, 'privacy')} target="_blank" rel="noopener noreferrer">
            {DOC_LABEL.privacy}
          </a>
          .
        </span>
      </label>
      {error && (
        <div role="alert" className={styles.errorAlert} data-testid="legal-gate-error">
          {error}
        </div>
      )}
    </Modal>
  );
}

function LegalVersionList({ current }: { current: LegalCurrentResponse | null }) {
  if (!current) {
    return <p className={styles.subtle}>Loading the latest versions…</p>;
  }
  const rows = DOC_ORDER
    .map((dt) => ({ dt, doc: current.documents[dt] }))
    .filter((r) => r.doc?.published && r.doc.version);
  if (rows.length === 0) return null;
  return (
    <div className={styles.versionList} aria-label="Current document versions">
      {rows.map(({ dt, doc }) => (
        <div key={dt} className={styles.versionRow}>
          <span>{DOC_LABEL[dt]}</span>
          <span>version {doc.version}</span>
        </div>
      ))}
    </div>
  );
}
